import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { toast } from "react-toastify";
import {
    postNotificationChangeSendType,
    postNotificationDelete,
    postNotificationSend,
} from "../../../../../../../utils/apiUtils";
import { exportToCsv } from "../../../../../../../utils/downloadUtils";
import {
    NOTIFICATION_DETAILS_QUERY_KEY_ROOT,
    NOTIFICATIONS_PAGE_ERROR_TOAST_OPTIONS,
    SendType,
    getNotificationDisplayTimestamp,
    getSentOrScheduledTimestampColumnLabelForNotification,
} from "../../../constants";
import { formatNotificationLocaleDateTime } from "../../helpers";

/**
 * TanStack mutations and CSV download for a single notification in the details sheet.
 * Confirmation UI and disabled-state flags live in {@link NotificationDetailsSheetBottomBar}.
 *
 * @param {object} params
 * @param {string} params.sendType Selected send type (`automated` | `manual`) — drives change-send-type target.
 * @param {string | null} params.notificationId Selected notification id.
 * @param {Record<string, unknown> | null} params.listRow Selected queue row (id fallback, CSV columns).
 * @param {Record<string, unknown> | null | undefined} params.details Loaded details for CSV and fallbacks.
 * @param {string | null} params.queueFilter Active queue tab ({@link QueueFilter}).
 * @param {unknown} params.status Notification status for CSV column label when on All tab.
 * @param {() => void} params.refreshNotificationsList Refetches list + count for current filters.
 * @param {() => void} [params.onDeleteSuccess] After successful delete (e.g. close drawer).
 * @returns {{
 *   moveSendTypeMutation: import('@tanstack/react-query').UseMutationResult<unknown, Error, void, unknown>,
 *   sendNowMutation: import('@tanstack/react-query').UseMutationResult<unknown, Error, void, unknown>,
 *   deleteMutation: import('@tanstack/react-query').UseMutationResult<unknown, Error, void, unknown>,
 *   onDownloadCsvFile: () => void,
 * }}
 */
export function useSingleNotificationActions({
    sendType,
    notificationId,
    listRow,
    details,
    queueFilter,
    status,
    refreshNotificationsList,
    onDeleteSuccess,
}) {
    const queryClient = useQueryClient();
    const isAutomated = sendType === SendType.AUTOMATED;
    const resolvedNotificationId = notificationId || listRow?.id;

    const invalidateDetailsAndRefresh = useCallback(async () => {
        if (resolvedNotificationId) {
            await queryClient.invalidateQueries({
                queryKey: [
                    NOTIFICATION_DETAILS_QUERY_KEY_ROOT,
                    resolvedNotificationId,
                ],
                exact: true,
            });
        }
        refreshNotificationsList();
    }, [queryClient, refreshNotificationsList, resolvedNotificationId]);

    const moveSendTypeMutation = useMutation({
        mutationFn: () =>
            postNotificationChangeSendType({
                notificationId: resolvedNotificationId,
                sendType: isAutomated ? SendType.MANUAL : SendType.AUTOMATED,
            }),
        onSuccess: async () => {
            await invalidateDetailsAndRefresh();
        },
        onError: () => {
            toast.error(
                "Could not change send type for this notification.",
                NOTIFICATIONS_PAGE_ERROR_TOAST_OPTIONS,
            );
        },
    });

    const sendNowMutation = useMutation({
        mutationFn: () =>
            postNotificationSend({
                notificationId: resolvedNotificationId,
            }),
        onSuccess: async () => {
            await invalidateDetailsAndRefresh();
        },
        onError: () => {
            toast.error(
                "Could not send this notification.",
                NOTIFICATIONS_PAGE_ERROR_TOAST_OPTIONS,
            );
        },
    });

    const deleteMutation = useMutation({
        mutationFn: () =>
            postNotificationDelete({
                notificationId: resolvedNotificationId,
            }),
        onSuccess: () => {
            refreshNotificationsList();
            onDeleteSuccess?.();
        },
        onError: () => {
            toast.error(
                "Could not delete this notification.",
                NOTIFICATIONS_PAGE_ERROR_TOAST_OPTIONS,
            );
        },
    });

    const onDownloadCsvFile = useCallback(() => {
        const timestampColumnLabel =
            getSentOrScheduledTimestampColumnLabelForNotification(
                queueFilter,
                status,
            );
        const row = {
            Client: listRow?.client || "",
            Template: listRow?.template || "",
            Type: listRow?.type || "",
            Status: listRow?.status || details?.status || "",
            Plan: listRow?.plan || "",
            "Member ID": details?.recipientId || "",
            "Member First Name": details?.memberFirstName || "",
            "Member Last Name": details?.memberLastName || "",
            "Member Date of Birth":
                formatNotificationLocaleDateTime(details?.memberDob, {
                    includeTime: false,
                }) || "",
            "Eligibility Type": details?.eligibilityType || "",
            "PCP First Name": details?.pcpFirstName || "",
            "PCP Last Name": details?.pcpLastName || "",
            "PCP NPI": details?.pcpNpi || "",
            Network: listRow?.network || "",
            [timestampColumnLabel]: formatNotificationLocaleDateTime(
                getNotificationDisplayTimestamp(
                    { ...listRow, ...details, status: status ?? listRow?.status ?? details?.status },
                    status,
                ),
            ),
        };

        exportToCsv({
            items: [row],
            headers: Object.keys(row),
            fileNamePrefix: `notification-${String(
                resolvedNotificationId ?? "unknown",
            ).slice(0, 8)}`,
        });
    }, [
        details,
        listRow,
        queueFilter,
        resolvedNotificationId,
        status,
    ]);

    return {
        moveSendTypeMutation,
        sendNowMutation,
        deleteMutation,
        onDownloadCsvFile,
    };
}
